import EstadoPruebaTarjeta from './EstadoPruebaTarjeta';
import { useEffect, useState } from "react";
import PlusBadge from "./PlusBadge";
import Button from "./foundational/Button";
import { diasRestantesTrial, finTrial } from "../lib/planGating";
import { CTA_MEMBRESIA } from "../lib/productosPlus";
import { isNative } from "../lib/platform";

/**
 * AvisoTrial — contador visible de los días que quedan de la prueba.
 * Se muestra en el Home durante la prueba y, en web, después de vencer, si el lanzamiento
 * abierto ya cerró (mientras el acceso está liberado para todos, el contador
 * confundiría). Evita la "sorpresa del día 8": el usuario siempre sabe cuánto
 * le queda y puede activar el plan antes de chocar con el paywall.
 *
 * Auditoría de conversión del 25-oct-2026: dice que al terminar no se cobra
 * (la prueba no se convierte en suscripción) y el botón es el mismo de todas
 * las puertas a Planes en la web. Antes decía "Ver planes" salvo al
 * estudiante en su último día.
 */
export default function AvisoTrial({ profile, onPlanes, onPagoConfirmado }) {
  const [ahora, setAhora] = useState(Date.now);
  const fin = finTrial(profile);

  // El vencimiento también debe verse con el inicio abierto, sin exigir una
  // recarga. Al volver de otra pestaña se corrige un temporizador suspendido.
  useEffect(() => {
    const refrescar = () => setAhora(Date.now());
    refrescar();
    window.addEventListener("focus", refrescar);
    document.addEventListener("visibilitychange", refrescar);
    const intervalo = Number.isFinite(fin) && fin > Date.now()
      ? window.setInterval(refrescar, 60000) : null;
    const vencimiento = Number.isFinite(fin) && fin > Date.now()
      ? window.setTimeout(refrescar, Math.min(fin - Date.now(), 2147483647)) : null;
    return () => {
      window.clearInterval(intervalo);
      window.clearTimeout(vencimiento);
      window.removeEventListener("focus", refrescar);
      document.removeEventListener("visibilitychange", refrescar);
    };
  }, [fin]);

  // No se invita a contratar a quien ya tiene una membresía vigente, aunque
  // haya cambiado de rol desde la prueba, ni a una cuenta TENS (es gratuita).
  const pagada = profile?.plan === "premium"
    && (!profile.premium_until || Date.parse(profile.premium_until) > ahora);
  if (pagada || !["estudiante", "enfermero"].includes(profile?.rol)) return null;

  if (profile?.trial_con_tarjeta) return isNative ? null : (
    <div className="max-w-[640px] mx-auto px-4 mt-3"><EstadoPruebaTarjeta onPagoConfirmado={onPagoConfirmado} /></div>
  );
  const dias = diasRestantesTrial(profile, ahora);
  const terminada = Number.isFinite(fin) && fin <= ahora;
  if (terminada) {
    // La venta sigue exclusivamente en la web; la app instalada conserva su
    // comportamiento. El botón abre las condiciones y el pago existentes.
    if (isNative || !onPlanes) return null;
    return (
      <div className="max-w-[640px] mx-auto px-4 mt-3">
        <section aria-labelledby="trial-terminada" className="rounded-tk-md border border-plus-border bg-plus-tint/30 px-3.5 py-3">
          <h2 id="trial-terminada" className="text-body font-semibold text-ink font-sans">Tu prueba terminó</h2>
          <p className="mt-1 text-small text-ink-subtle font-sans leading-snug">
            Ahora estás en el plan gratis. No se te cobró nada y lo que creaste sigue guardado.
          </p>
          <p className="mt-2 mb-3 text-small text-ink-subtle font-sans leading-snug">
            Para seguir usando los beneficios de Plus, contrata una membresía.
          </p>
          <Button variant="primary" size="sm" onClick={() => onPlanes("trial_vencido_home")}>
            Contratar membresía
          </Button>
        </section>
      </div>
    );
  }
  if (dias <= 0) return null;

  const ultimo = dias === 1;

  return (
    <div className="max-w-[640px] mx-auto px-4 mt-3">
      <div className={`flex items-center gap-3 rounded-tk-md border px-3.5 py-2.5 ${ultimo ? "border-plus bg-plus-tint/60" : "border-plus-border bg-plus-tint/30"}`}>
        <PlusBadge size="sm" className="flex-shrink-0" />
        <p className="flex-1 min-w-0 text-12.5px text-ink-subtle font-sans leading-snug">
          {ultimo ? (
            <>Tu prueba termina <span className="font-semibold text-ink">en menos de 24 horas</span>.</>
          ) : (
            <>Te quedan <span className="font-semibold text-ink">{dias} días</span> de prueba.</>
          )}
          <span className="text-ink-muted"> Al terminar no se cobra nada. Vuelves al plan gratis y lo que creaste sigue guardado.</span>
          {/* Dentro del texto, para que en un teléfono angosto el botón no le
              robe el ancho al aviso. Solo existe en la web (App no pasa
              onPlanes en la app instalada). */}
          {!isNative && onPlanes && (
            <>
              {" "}
              <button
                type="button"
                onClick={() => onPlanes("trial_home")}
                className="inline-flex min-h-[44px] items-center align-middle text-12px font-semibold text-plus hover:underline font-sans"
              >
                {CTA_MEMBRESIA} →
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}

